import { useMemo, useState, type CSSProperties } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { ThemeToggleSimple } from "@/components/ui/theme-toggle-simple";
import { paymentApi } from "@/lib/api";
import "@/styles/payment-reconciliation.css";
import { ActivityTab } from "./ActivityTab";
import { AlertsTab } from "./AlertsTab";
import { StudentActivityTab } from "./StudentActivityTab";
import { CheckBalanceTab } from "./CheckBalanceTab";
import { UpcomingPaymentsTab } from "./UpcomingPaymentsTab";

export type PaymentTabId = "activity" | "alerts" | "student" | "balance" | "upcoming";

const TABS: Array<{ id: PaymentTabId; label: string }> = [
  { id: "activity", label: "Activity" },
  { id: "alerts", label: "Alerts" },
  { id: "student", label: "Student Activity" },
  { id: "balance", label: "Check Balance" },
  { id: "upcoming", label: "Upcoming" },
];

const TAB_INDEX: Record<PaymentTabId, number> = {
  activity: 0,
  alerts: 1,
  student: 2,
  balance: 3,
  upcoming: 4,
};

export function PaymentReconciliationPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = (searchParams.get("tab") as PaymentTabId) || "activity";
  const activeTab: PaymentTabId = TABS.some((t) => t.id === tabParam) ? tabParam : "activity";
  const studentSearch = searchParams.get("search") ?? "";

  const [isReconciling, setIsReconciling] = useState(false);
  const [isSyncingSquare, setIsSyncingSquare] = useState(false);

  const tabStyle = useMemo(
    () => ({ "--pr-tab-index": TAB_INDEX[activeTab] } as CSSProperties),
    [activeTab]
  );

  const setTab = (tab: PaymentTabId) => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", tab);
    if (tab !== "student") next.delete("search");
    setSearchParams(next, { replace: true });
  };

  const handleReconcile = async () => {
    setIsReconciling(true);
    try {
      const result = await paymentApi.reconcilePayments();
      const success = result.data?.success ?? (result as { success?: boolean }).success;
      const results = result.data?.results ?? (result as { results?: { ordersFixed?: number } }).results;
      if (success) {
        toast.success(
          results
            ? `Reconciliation completed: ${results.ordersFixed ?? 0} orders fixed`
            : "Payment-order reconciliation completed"
        );
      } else {
        toast.error("Reconciliation failed");
      }
    } catch {
      toast.error("An error occurred during reconciliation");
    } finally {
      setIsReconciling(false);
    }
  };

  const handleSyncFromSquare = async () => {
    setIsSyncingSquare(true);
    try {
      const result = await paymentApi.syncPaymentsFromSquare({ dryRun: false });
      toast.success(result.message || "Square sync completed");
    } catch {
      toast.error("Failed to sync payments from Square");
    } finally {
      setIsSyncingSquare(false);
    }
  };

  return (
    <div className="pr-shell">
      <header className="pr-topbar">
        <div className="pr-topbar-tools">
          <SidebarTrigger className="scale-125" />
          <ThemeToggleSimple />
        </div>
        <div className="pr-topbar-inner">
          <nav className="pr-page-tabs" style={tabStyle}>
            {TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                className={`pr-tab-btn ${activeTab === tab.id ? "active" : ""}`}
                onClick={() => setTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </nav>
          <div className="pr-system-title">
            <div>
              <span className="pr-title-kicker">Reconciliation System</span>
              <h1>Payment Reconciliation</h1>
              <p>
                Check payment overview, detailed activity, alerts, and reconcile school payments against the
                payment provider.
              </p>
              <div className="pr-header-actions">
                <button
                  type="button"
                  className="pr-pill-btn"
                  onClick={() => void handleSyncFromSquare()}
                  disabled={isSyncingSquare}
                >
                  {isSyncingSquare ? (
                    <Loader2 className="inline h-4 w-4 animate-spin mr-1" />
                  ) : (
                    <RefreshCw className="inline h-4 w-4 mr-1" />
                  )}
                  Sync from Square
                </button>
                <button
                  type="button"
                  className="pr-pill-btn"
                  onClick={() => void handleReconcile()}
                  disabled={isReconciling}
                >
                  {isReconciling ? (
                    <Loader2 className="inline h-4 w-4 animate-spin mr-1" />
                  ) : (
                    <RefreshCw className="inline h-4 w-4 mr-1" />
                  )}
                  Reconcile Orders
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="pr-main">
        {activeTab === "activity" && <ActivityTab />}
        {activeTab === "alerts" && <AlertsTab />}
        {activeTab === "student" && <StudentActivityTab initialSearch={studentSearch} />}
        {activeTab === "balance" && <CheckBalanceTab />}
        {activeTab === "upcoming" && <UpcomingPaymentsTab />}
      </main>
    </div>
  );
}
